import { useState, useCallback } from 'react'
import { ClaimSearchPage } from './Claim/Search/ClaimSearchPage'
import { ClaimDetailsPage } from './Claim/ClaimDetailsPage'
import { theme } from '../theme'
import { MantineProvider, Skeleton, Stack, Alert, Button, Center } from '@mantine/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation, faRotate } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { useOfficeTheme } from '../hooks/useOfficeTheme'
import { useEmailClaimDetection } from '../hooks/useEmailClaimDetection'
import '@mantine/core/styles.css'

const App: React.FC = () => {
  const [selectedClaim, setSelectedClaim] = useState<string | null>(null)
  const colorScheme = useOfficeTheme()
  const detection = useEmailClaimDetection()

  // Derive the active claim: user-selected claim takes priority, then auto-detected
  const activeClaim = selectedClaim ?? (detection.status === 'found' ? detection.claimNumber : null)

  const handleConnect = useCallback((claim: { claimNumber: string } | null) => {
    setSelectedClaim(claim?.claimNumber ?? null)
  }, [])

  const handleBack = () => {
    handleConnect(null)
    detection.reset()
  }

  const detectionMessage =
    detection.status === 'not-found' && detection.detectedReference
      ? `Reference '${detection.detectedReference}' detected but no matching claim found.`
      : undefined

  const isDetecting = detection.status === 'idle' || detection.status === 'detecting'

  const renderContent = () => {
    if (activeClaim) {
      return <ClaimDetailsPage claimNumber={activeClaim} onBack={handleBack} />
    }
    if (isDetecting) {
      return (
        <Stack gap="md" p="md" mih="100vh">
          <Skeleton height={30} width="60%" />
          <Skeleton height={36} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={80} />
          ))}
        </Stack>
      )
    }

    if (detection.status === 'error') {
      return (
        <Center mih="100vh" p="md">
          <Alert
            icon={<FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: '1rem' }} />}
            title="Connection Error"
            color="red"
            variant="light"
            styles={{
              root: { width: '100%' },
            }}
          >
            <Stack gap="sm">
              <div>{detection.errorMessage}</div>
              <Button
                leftSection={<FontAwesomeIcon icon={faRotate} style={{ fontSize: '1rem' }} />}
                onClick={detection.retry}
                variant="outline"
                color="red"
                size="xs"
                fullWidth
              >
                Retry
              </Button>
            </Stack>
          </Alert>
        </Center>
      )
    }

    return (
      <ClaimSearchPage
        onConnect={handleConnect}
        connectedClaimNumber={activeClaim ?? undefined}
        highlightClaimNumber={detection.highlightClaimNumber ?? undefined}
        detectionMessage={detectionMessage}
      />
    )
  }

  return (
    <MantineProvider theme={theme} forceColorScheme={colorScheme}>
      {renderContent()}
    </MantineProvider>
  )
}

export default App
